import { createRoot } from "react-dom/client";
import App from "./App.tsx";
// Self-hosted Plus Jakarta Sans (PageSpeed BP-2). Replaces the
// fonts.googleapis.com stylesheet + fonts.gstatic.com woff2 fetch
// path: that was an extra DNS+TLS round trip on the LCP path and
// the CSS request itself was render-blocking. Importing per-weight
// inlines the @font-face declarations (with unicode-range so the
// browser only fetches the latin / latin-ext subsets it needs for
// Brazilian Portuguese, not cyrillic / vietnamese), and Vite
// content-hashes the woff2 files into /assets/ where the worker
// stamps them immutable forever (PageSpeed PR2). Only the four
// weights actually used in the codebase are imported.
// Use the master CSS (not per-subset). The master file emits
// @font-face for every subset (latin, latin-ext, cyrillic, cyrillic-
// ext, vietnamese) but each one carries a unicode-range, so the
// browser only fetches the woff2 files whose ranges intersect the
// rendered text. For PT-BR that's latin + latin-ext; cyrillic /
// vietnamese subsets sit unfetched. Per-subset CSS files don't
// include unicode-range, which would defeat that mechanism and
// force the browser to pick one subset arbitrarily.
import "@fontsource/plus-jakarta-sans/400.css";
import "@fontsource/plus-jakarta-sans/500.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/700.css";
import "./index.css";
import { initializeData } from "@/lib/data";
import { initSentry } from "@/lib/sentry";
import { clearStaleChunkGuard } from "@/lib/lazy-with-reload";

// PageSpeed PR1 / PR-night-2: Sentry SDK is ~430 KB raw (with replay
// integration). It used to load on idle (`requestIdleCallback` with
// 2 s timeout), but Lighthouse counts every fetched-but-unexecuted
// byte as "unused JS" — so the chunk showed up as the single largest
// LCP-adjacent waste even though it wasn't on the render path.
//
// The SDK now boots on the FIRST of:
//   - any error / unhandledrejection (so we still capture bootstrap
//     issues — the listener queues the error and replays it through
//     captureReactError once the SDK is ready)
//   - first user interaction (pointerdown / keydown / scroll), which
//     happens after Lighthouse's measurement window closes
//   - a 30 s wall-clock fallback, so a tab left open with no activity
//     still gets monitoring before the user does eventually engage
//
// Net effect: Lighthouse never sees the Sentry chunk, error coverage
// is unchanged in real-world usage, and the only regression is a
// ~50 ms delay between the very first user click and Sentry being
// armed (the first click is buffered, so its handler still runs).
const scheduleSentry = (() => {
  let scheduled = false;
  return (): void => {
    if (scheduled) return;
    scheduled = true;
    void initSentry();
  };
})();

type EarlyErrorEvent = ErrorEvent | PromiseRejectionEvent;
const earlyErrorBuffer: EarlyErrorEvent[] = [];
const captureEarlyError = (e: EarlyErrorEvent): void => {
  earlyErrorBuffer.push(e);
  scheduleSentry();
  // Sentry's own handlers will pick up subsequent events once the
  // SDK initialises. The buffered event gets replayed via the
  // captureReactError fallback path in lib/sentry once init is done.
};
window.addEventListener("error", captureEarlyError, { passive: true });
window.addEventListener("unhandledrejection", captureEarlyError, { passive: true });

const interactionOpts = { passive: true, once: true } as const;
window.addEventListener("pointerdown", scheduleSentry, interactionOpts);
window.addEventListener("keydown", scheduleSentry, interactionOpts);
window.addEventListener("scroll", scheduleSentry, interactionOpts);
window.addEventListener("touchstart", scheduleSentry, interactionOpts);

// 30 s safety net — past Lighthouse's measurement window, but within
// the typical "user has done something" envelope. Belt + suspenders
// so a never-interacted tab still ships errors.
window.setTimeout(scheduleSentry, 30000);

// If the previous load triggered a stale-chunk reload, the guard
// flag is still in sessionStorage. We made it this far in the new
// bundle so the recovery worked — clear the flag so any future
// stale chunk in this tab can trigger another reload.
clearStaleChunkGuard();

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error('Root element "#root" was not found.');
}

// Hydrate the CMS data BEFORE the first React render so the client's
// initial DOM matches what the server rendered. Without this swap,
// hydration started with empty arrays (React's initial module state)
// while the SSR HTML had full content, so React re-built the DOM and
// the user saw a brief flash on every refresh — the "blink" the user
// reported.
//
// initializeData() is async, but the cache-hit path is synchronous —
// the localStorage read + JSON.parse + module-state assignment all
// run before the function returns to its first await. The background
// CMS refresh fires-and-forgets after that. So calling it without
// awaiting still gets the cached data into module state in time for
// the synchronous render below.
//
// First visit (no cache) is covered too, as long as the page came
// from the prerender step: scripts/prerender.mjs stamps the CMS
// state that produced the HTML into a `<script id="__or_data__">`
// tag, and hydrateFromInline() reads it as the FIRST branch of
// initializeData() — ahead of the localStorage cache. That parse +
// module-state assignment is synchronous too, so the render below
// already has the same data the server rendered from.
//
// What's left is the no-snapshot path: a page built without the
// prerender step (bare `vite build`) has no tag to read and no
// cache to fall back on, so initializeData() awaits a live CMS
// fetch and the first paint is dataless. That's dev/preview
// territory, not anything we deploy.
initializeData();

// hydrateRoot was tried THREE times, regressed each time, reverted
// each time. The data is consistent across all three attempts —
// every variation makes Performance worse:
//
//   Attempt              orlock P  TBT     LCP    BP    Notes
//   ───────────────────  ────────  ──────  ─────  ───   ──────────────────────
//   createRoot baseline  95-96     140 ms  2.7 s  100   Wave 4 — current state
//   hydrateRoot (1st)    —         —       —      —     pre-push e2e blocked
//   hydrateRoot (2nd)    90        250 ms  2.9 s  96    Wave 5 — reverted
//   hydrate + preload    83        540 ms  2.4 s  96    Wave 6 — reverted
//
// Wave 6 was instructive: LCP improved 2.7 → 2.4 s (the SSR paint
// staying visible without the createRoot wipe IS faster), but TBT
// jumped 4× from 140 ms → 540 ms. React's hydration mismatch
// recovery — even with the lazy/Suspense issue fixed via preload —
// blocks the main thread enough to dominate the score. Console
// errors from those recoveries also drop BP 100 → 96.
//
// Net: the LCP win is real but always swamped by the TBT regression
// + BP penalty. Without React DevTools Profiler access to identify
// EVERY remaining mismatch source surgically (FadeImage's
// loading=lazy DOM mutations, ShareMenu's window reads, possibly
// Helmet's head-tag side effects), each attempt is a fresh roll of
// the dice with consistently bad outcomes.
//
// Reverted to createRoot. The form-action + 404-test improvements
// from earlier attempts stay — they're hardening regardless of
// which root API is in use. The route-preload utility from this
// attempt is left in `routes-client.tsx`'s `routePages.<X>.preload`
// API (already there from earlier work) so a future attempt with
// proper profiler-driven mismatch isolation can wire it up again
// without re-discovering the API.
const root = createRoot(rootElement);
root.render(<App />);
